/** Every read the console makes, as TanStack Query hooks with shared keys.
 *
 *  Screens read through these instead of useEffect + useState, so a write in
 *  one place refreshes every screen showing it: a mutation invalidates the
 *  keys below. The keys are the ones the pages from before the redesign use
 *  (['tracking-status'], ['apps'], ['playslots'] …), so an old page and a new
 *  one share the cache while both are mounted. */

import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, type AnalysisPeriod, type AttentionDismissal, type JobRun, type KeywordAnalysisOptions } from '@/api/client'
import { WATCHED_JOBS } from './attention'

export { useJobRuns } from '@/admin-design/_common/jobs/api'

/** The live screens (today, the attention strip) poll this often. */
export const LIVE_MS = 30_000
/** The tracker's status while a snapshot is running. */
export const RUNNING_MS = 5_000

export const qk = {
  me: ['me'] as const,
  version: ['version'] as const,
  dashboard: ['dashboard'] as const,
  trackingStatus: ['tracking-status'] as const,
  trackingOverview: ['tracking-overview'] as const,
  apps: ['apps'] as const,
  app: (id: number) => ['app', id] as const,
  keywords: (appId: number) => ['keywords', appId] as const,
  keywordHistory: (id: number, limit: number) => ['keyword-history', id, limit] as const,
  competitors: (appId: number) => ['competitors', appId] as const,
  funnelSources: (appId: number, days: number) => ['funnel-sources', appId, days] as const,
  listingHistory: (appId: number) => ['listing-history', appId] as const,
  analysis: (appId: number) => ['analysis', appId] as const,
  analyses: (includeCompetitors: boolean) => ['analyses', includeCompetitors] as const,
  changes: ['changes'] as const,
  listingEffects: ['listing-effects'] as const,
  trackedKeywords: ['tracked-keywords'] as const,
  keywordCompetition: (id: number) => ['kw-competition', id] as const,
  keywordAnalysis: (period: AnalysisPeriod, opts: KeywordAnalysisOptions = {}) => ['kw-analysis', period, opts] as const,
  trackedKeywordSearch: (q: string, limit: number) => ['tracked-keywords', 'search', q, limit] as const,
  analyticsStatus: ['analytics-status'] as const,
  analyticsApps: (days: number, platform: string) => ['analytics-apps', days, platform] as const,
  analyticsQuality: (days: number) => ['analytics-quality', days] as const,
  analyticsBreakdown: (days: number, platform: string) => ['analytics-breakdown', days, platform] as const,
  playSlots: ['playslots'] as const,
  attentionDismissals: ['attention-dismissals'] as const,
  webUsageList: (days: number, internal: boolean) => ['webusage-list', days, internal] as const,
  webUsageReport: (id: string, days: number, internal: boolean) => ['webusage-report', id, days, internal] as const,
  webUsageMap: (id: string) => ['webusage-map', id] as const,
  reports: ['reports'] as const,
  report: (date: string) => ['report', date] as const,
  insight: (from: string, to: string) => ['insight', from, to] as const,
  revenueStatus: ['revenue-status'] as const,
}

/** What a change to a keyword (added, analyzed, deleted) makes stale: the
 *  app's keyword list, the rank history, and every screen that counts or lists
 *  keywords across apps. The keyword drawer and the keyword tab both call this,
 *  so a change made in one shows in the other. */
export function invalidateKeywordData(qc: QueryClient, appId: number, keywordId?: number) {
  const keys: (readonly unknown[])[] = [
    qk.keywords(appId),
    qk.dashboard,
    qk.trackingOverview,
    qk.trackedKeywords,
    ['kw-analysis'],
    ...(keywordId ? [['keyword-history', keywordId]] : []),
  ]
  for (const queryKey of keys) qc.invalidateQueries({ queryKey })
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.me, staleTime: 5 * 60_000 })
}

/** The build the server runs, for the sidebar footer. */
export function useVersion() {
  return useQuery({ queryKey: qk.version, queryFn: api.version, staleTime: Infinity })
}

export function useDashboard() {
  return useQuery({ queryKey: qk.dashboard, queryFn: api.dashboard, refetchInterval: LIVE_MS })
}

/** The rank tracker's state. Polled every 5 seconds while a snapshot runs,
 *  every 30 otherwise.
 *
 *  When a snapshot ends (running true → false between two answers) every other
 *  query is refreshed, so any mounted view picks up the new ranks, popularity
 *  and scores without a reload. The check sits in the query function, so it
 *  fires once per answer however many components watch the status. */
export function useTrackingStatus() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: qk.trackingStatus,
    queryFn: async () => {
      const before = qc.getQueryData<{ running: boolean }>(qk.trackingStatus)
      const next = await api.trackingStatus()
      if (before?.running && !next.running) {
        // After the answer is stored, and not the status itself: refetching it
        // from inside its own fetch would cancel the fetch and loop.
        setTimeout(() => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== qk.trackingStatus[0] }), 0)
      }
      return next
    },
    refetchInterval: (q) => (q.state.data?.running ? RUNNING_MS : LIVE_MS),
  })
}

export function useTrackingOverview() {
  return useQuery({ queryKey: qk.trackingOverview, queryFn: api.trackingOverview })
}

export function useApps() {
  return useQuery({ queryKey: qk.apps, queryFn: api.listApps })
}

export function useApp(id: number) {
  return useQuery({ queryKey: qk.app(id), queryFn: () => api.getApp(id), enabled: id > 0 })
}

export function useKeywords(appId: number) {
  return useQuery({ queryKey: qk.keywords(appId), queryFn: () => api.listKeywords(appId), enabled: appId > 0 })
}

/** The app's funnel split by Apple's source type. */
export function useFunnelSources(appId: number, days = 28) {
  return useQuery({ queryKey: qk.funnelSources(appId, days), queryFn: () => api.funnelSources(appId, days), enabled: appId > 0 })
}

/** What the app's listing said, change by change. */
export function useListingHistory(appId: number) {
  return useQuery({ queryKey: qk.listingHistory(appId), queryFn: () => api.listingHistory(appId), enabled: appId > 0 })
}

/** One keyword's rank history, up to 60 snapshots (the keyword drawer). */
export function useKeywordHistory(id: number, limit = 60) {
  return useQuery({ queryKey: qk.keywordHistory(id, limit), queryFn: () => api.keywordHistory(id, limit), enabled: id > 0 })
}

export function useCompetitors(appId: number) {
  return useQuery({ queryKey: qk.competitors(appId), queryFn: () => api.listCompetitors(appId), enabled: appId > 0 })
}

/** An app's market score. No analysis yet answers null, as the old tab did. */
export function useAnalysis(appId: number) {
  return useQuery({
    queryKey: qk.analysis(appId),
    queryFn: () => api.getAnalysis(appId).catch(() => null),
    enabled: appId > 0,
  })
}

export function useAnalyses(includeCompetitors = false) {
  return useQuery({ queryKey: qk.analyses(includeCompetitors), queryFn: () => api.listAnalyses(includeCompetitors) })
}

/** The change log, newest first. */
export function useChanges() {
  return useQuery({ queryKey: qk.changes, queryFn: () => api.changes(50) })
}

/** The checks of recent listing changes, newest change first. */
export function useListingEffects() {
  return useQuery({ queryKey: qk.listingEffects, queryFn: () => api.listingEffects() })
}

export function useTrackedKeywords() {
  return useQuery({ queryKey: qk.trackedKeywords, queryFn: api.listTrackedKeywords })
}

/** Tracked keywords whose term contains `q`, at most `limit` (the 키워드
 *  찾기 box on 변동). */
export function useTrackedKeywordSearch(q: string, limit = 20, enabled = true) {
  return useQuery({
    queryKey: qk.trackedKeywordSearch(q, limit),
    queryFn: () => api.listTrackedKeywords(q, limit),
    enabled,
    placeholderData: keepPreviousData,
  })
}

/** The top-10 board for one keyword over the last 14 snapshots. */
export function useKeywordCompetition(id: number) {
  return useQuery({ queryKey: qk.keywordCompetition(id), queryFn: () => api.keywordCompetition(id), enabled: id > 0 })
}

/** One page of the rank-movement analysis. `opts` narrows and pages it on the
 *  server; `summary` and `apps` in the answer stay whole-portfolio. */
export function useKeywordAnalysis(period: AnalysisPeriod = 'weekly', opts: KeywordAnalysisOptions = {}) {
  return useQuery({
    queryKey: qk.keywordAnalysis(period, opts),
    queryFn: () => api.keywordAnalysis(period, opts),
    placeholderData: keepPreviousData,
  })
}

/** The analytics collector's pull state. Feeds the attention strip. */
export function useAnalyticsStatus(live = false) {
  return useQuery({
    queryKey: qk.analyticsStatus,
    queryFn: () => api.analyticsStatus(),
    refetchInterval: live ? LIVE_MS : undefined,
  })
}

export function useAnalyticsApps(days = 30, platform = '') {
  return useQuery({
    queryKey: qk.analyticsApps(days, platform),
    queryFn: () => api.analyticsApps(days, platform),
    placeholderData: keepPreviousData,
  })
}

export function useAnalyticsQuality(days = 14) {
  return useQuery({ queryKey: qk.analyticsQuality(days), queryFn: () => api.analyticsQuality(days) })
}

export function useAnalyticsBreakdown(days = 30, platform = '') {
  return useQuery({
    queryKey: qk.analyticsBreakdown(days, platform),
    queryFn: () => api.analyticsBreakdown(days, platform),
    placeholderData: keepPreviousData,
  })
}

/** Play Console slot stock, its summary and the attempt log. */
export function usePlaySlots() {
  return useQuery({ queryKey: qk.playSlots, queryFn: api.playSlots, refetchInterval: LIVE_MS })
}

/** The 확인할 것 items cleared by hand, polled like the items themselves so a
 *  clear on the phone reaches the desktop. */
export function useAttentionDismissals() {
  return useQuery({ queryKey: qk.attentionDismissals, queryFn: api.attentionDismissals, refetchInterval: LIVE_MS })
}

/** Clears one 확인할 것 item. It leaves the list at once; a failed save puts
 *  it back. */
export function useDismissAttention() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, stamp }: { key: string; stamp: string }) => api.dismissAttention(key, stamp),
    onMutate: async ({ key, stamp }) => {
      await qc.cancelQueries({ queryKey: qk.attentionDismissals })
      const before = qc.getQueryData<{ items: AttentionDismissal[] }>(qk.attentionDismissals)
      const rest = (before?.items ?? []).filter((d) => d.key !== key)
      qc.setQueryData(qk.attentionDismissals, { items: [{ key, stamp, dismissed_at: Math.floor(Date.now() / 1000) }, ...rest] })
      return { before }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(qk.attentionDismissals, ctx.before)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.attentionDismissals }),
  })
}

/** The newest runs of the tracker and of every job the attention items watch,
 *  read job by job. One shared "latest 50" list does not do: in production
 *  analytics_pull runs every few minutes and fills it, which hid the last
 *  finished snapshot (오늘 said "마지막 없음") and every daily job's failure.
 *  The key has the shape the jobs page uses, so its invalidation reaches here. */
export function useWatchedRuns(): { runs: JobRun[] | undefined; error: unknown } {
  const results = useQueries({
    queries: ['tracking_snapshot', ...WATCHED_JOBS].map((job) => ({
      queryKey: ['job-runs', '/api/admin/jobs', job, 5] as const,
      queryFn: () => api.jobRuns(job, 5),
      refetchInterval: LIVE_MS,
    })),
  })
  if (results.some((r) => !r.data)) return { runs: undefined, error: results.find((r) => r.error)?.error }
  return { runs: results.flatMap((r) => r.data?.runs ?? []), error: null }
}

export function useReports() {
  return useQuery({ queryKey: qk.reports, queryFn: api.reports })
}

export function useReport(date: string) {
  return useQuery({ queryKey: qk.report(date), queryFn: () => api.report(date), enabled: !!date })
}

/** Rebuilds a day's report from today's data and puts the answer straight in
 *  the cache; the archive list refreshes for its revenue and time. */
export function useBuildReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => api.buildReport(date),
    onSuccess: (data, date) => {
      qc.setQueryData(qk.report(date), data)
      qc.invalidateQueries({ queryKey: qk.reports })
    },
  })
}

export function useInsight(from: string, to: string) {
  return useQuery({ queryKey: qk.insight(from, to), queryFn: () => api.insight(from, to), enabled: !!from && !!to })
}

export function useRevenueStatus() {
  return useQuery({ queryKey: qk.revenueStatus, queryFn: api.revenueStatus })
}

/** Collects the revenue sources now (the server answers when it is done,
 *  up to ten minutes). */
export function useCollectRevenue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: api.collectRevenue,
    onSettled: () => qc.invalidateQueries({ queryKey: qk.revenueStatus }),
  })
}
